import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Modal,
  Form,
  Select,
  Button,
  Row,
  Col,
  Divider,
  Alert,
  Spin,
  Switch,
  Typography,
  Checkbox,
} from 'antd';
import { get } from 'lodash';
import {
  fetchAllCompanies as fetchAllCompaniesAction,
  createIntegration as createIntegrationAction,
} from './actions';
import { normalizePathwayUCompanyId } from './util';
import './IntegrationModal.scss';

const { Option } = Select;
const { Text } = Typography;

const IntegrationModal = ({
  visible,
  hub,
  onClose,
  integrationLoading,
  success,
  createIntegration: createIntegrationProp,
  fetchAllCompanies: fetchAllCompaniesProp,
  companies,
  companiesLoading,
}) => {
  const [formData, setFormData] = useState({
    pathwayUCompanyId: null,
    userMigration: false,
    demographicDataMigration: false,
    workbooksWorksheetsMigration: false,
    resourcesMigration: false,
    users: false,
    counselors: false,
    standalone: false,
    discoveryItems: false,
    restrictAccess: false,
  });

  useEffect(() => {
    if (visible) {
      fetchAllCompaniesProp();
    }
  }, [visible, fetchAllCompaniesProp]);

  useEffect(() => {
    // Call onSuccess when integration is successful
    if (success && onClose) {
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  }, [success, onClose]);

  const handleFormChange = (field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleSaveAndMigrate = () => {
    const data = {
      hubId: hub.id,
      pathwayUCompanyId: normalizePathwayUCompanyId(formData.pathwayUCompanyId),
      userMigration: formData.userMigration,
      demographicDataMigration: formData.demographicDataMigration,
      workbooksWorksheetsMigration: formData.workbooksWorksheetsMigration,
      resourcesMigration: formData.resourcesMigration,
      standalone: formData.standalone,
      discoveryItems: formData.discoveryItems,
      restrictAccess: formData.restrictAccess,
    };

    createIntegrationProp(data);
  };

  return (
    <Modal
      title="Integrate PathwayU"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
      centered
      className="integration-modal__container"
    >
      <Spin spinning={companiesLoading}>
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="PG Hub Name">
                <div className="integration-modal__hub-name">{hub.name}</div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="PathwayU Hub/Company"
                required
                help="Select the PathwayU hub/company to integrate with this PG hub"
              >
                <Select
                  placeholder="Select PathwayU hub/company"
                  value={formData.pathwayUCompanyId}
                  onChange={value =>
                    handleFormChange('pathwayUCompanyId', value)
                  }
                  aria-label="Select PathwayU hub/company"
                  loading={companiesLoading}
                  showSearch
                  filterOption={(input, option) => {
                    const searchText = input.toLowerCase();
                    const optionText = Array.isArray(option.props.children)
                      ? option.props.children.join(' ').toLowerCase()
                      : option.props.children.toLowerCase();
                    return optionText.includes(searchText);
                  }}
                >
                  {companies.map(company => (
                    <Option
                      key={company.id}
                      value={company.id}
                      disabled={company.integrated}
                    >
                      {company.name} (ID: {company.id})
                      {company.domain && ` - ${company.domain}`}
                      {company.hasPrograms && ' - Has Programs'}
                      {company.integrated && ' - Already Integrated'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16]}>
            <Col span={24}>
              <Form.Item style={{ marginTop: '24px' }} valuePropName="checked">
                <div
                  style={{
                    display: 'flex',
                    columnGap: '16px',
                    alignItems: 'center',
                  }}
                >
                  <span>Standalone Hub</span>
                  <Switch
                    checked={formData.standalone}
                    onChange={checked => {
                      if (!checked) {
                        handleFormChange('discoveryItems', false);
                      }
                      handleFormChange('standalone', checked);
                    }}
                    aria-label="Mark as standalone hub"
                    size="small"
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col span={24}>
              <Typography.Title level={4}>Migration Options</Typography.Title>
            </Col>
          </Row>

          <Row gutter={[0, 4]}>
            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className="integration-modal__switch-row">
                  <div className="integration-modal__switch-label">
                    <Text strong>User Migration</Text>
                  </div>
                  <Switch
                    checked={formData.userMigration}
                    onChange={checked =>
                      handleFormChange('userMigration', checked)
                    }
                    aria-label="Toggle user migration"
                    size="small"
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[0, 4]}>
            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className="integration-modal__switch-row">
                  <div className="integration-modal__switch-label">
                    <Text strong>Demographic Data Migration</Text>
                  </div>
                  <Switch
                    checked={formData.demographicDataMigration}
                    onChange={checked =>
                      handleFormChange('demographicDataMigration', checked)
                    }
                    aria-label="Toggle demographic data migration"
                    disabled={!formData.userMigration}
                    size="small"
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[0, 4]}>
            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className="integration-modal__switch-row">
                  <div className="integration-modal__switch-label">
                    <Text strong>Workbooks and Worksheets Migration</Text>
                  </div>
                  <Switch
                    checked={formData.workbooksWorksheetsMigration}
                    onChange={checked =>
                      handleFormChange('workbooksWorksheetsMigration', checked)
                    }
                    aria-label="Toggle workbooks and worksheets migration"
                    size="small"
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[0, 4]}>
            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className="integration-modal__switch-row">
                  <div className="integration-modal__switch-label">
                    <Text strong>Resources Migration</Text>
                  </div>
                  <Switch
                    checked={formData.resourcesMigration}
                    onChange={checked =>
                      handleFormChange('resourcesMigration', checked)
                    }
                    aria-label="Toggle resources migration"
                    size="small"
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[0, 4]}>
            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className="integration-modal__switch-row">
                  <div className="integration-modal__switch-label">
                    <Text strong>Discovery items</Text>
                  </div>
                  <Switch
                    checked={formData.discoveryItems}
                    onChange={checked =>
                      handleFormChange('discoveryItems', checked)
                    }
                    aria-label="Discovery items"
                    size="small"
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[0, 4]}>
            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className="integration-modal__switch-row">
                  <div className="integration-modal__switch-label">
                    <Text strong>Restrict Access</Text>
                  </div>
                  <Switch
                    checked={formData.restrictAccess}
                    onChange={checked =>
                      handleFormChange('restrictAccess', checked)
                    }
                    aria-label="Restrict Access"
                    size="small"
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col span={24}>
              <Alert
                message="Data Migration"
                description="When you save and migrate, all selected PathwayU data will be copied to PG if not already present. This includes users, demographics, resources, and other relevant data based on your selections above."
                type="info"
                showIcon
              />
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col span={12}>
              <Button
                onClick={onClose}
                className="integration-modal__button"
                aria-label="Close modal"
              >
                Close
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                onClick={handleSaveAndMigrate}
                loading={integrationLoading}
                className="integration-modal__button"
                disabled={!formData.pathwayUCompanyId}
                aria-label="Save and migrate data"
              >
                Save and Migrate
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

const mapStateToProps = state => ({
  integrationLoading: get(
    state,
    'pathwayuIntegration.integrationLoading',
    false
  ),
  success: get(state, 'pathwayuIntegration.success', false),
  companies: get(state, 'pathwayuIntegration.companies', []),
  companiesLoading: get(state, 'pathwayuIntegration.companiesLoading', false),
});

const mapDispatchToProps = {
  fetchAllCompanies: fetchAllCompaniesAction,
  createIntegration: createIntegrationAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(IntegrationModal);
